/**
 * Form Field Palette (P2.1-S5 polish).
 *
 * Trước: list dọc với icon nhỏ + label.
 * Sau: 2-col grid card (icon top + label bottom) + tooltip — visual scan
 * nhanh hơn, dễ nhận diện type.
 */
import {
  Calendar,
  CalendarClock,
  CheckSquare,
  CircleDot,
  Database,
  FileText,
  Folder,
  Hash,
  List,
  ListChecks,
  Repeat,
  Sigma,
  type LucideIcon,
  Type,
  Upload,
} from "lucide-react";
import * as React from "react";

import type { FieldType } from "@/api/forms";
import { cn } from "@/lib/utils";
import { FIELD_TYPE_LABELS, useFormBuilderStore } from "@/stores/formBuilder";

interface PaletteItem {
  type: FieldType;
  Icon: LucideIcon;
  group: "basic" | "advanced" | "layout";
}

const ITEMS: PaletteItem[] = [
  { type: "text", Icon: Type, group: "basic" },
  { type: "textarea", Icon: FileText, group: "basic" },
  { type: "number", Icon: Hash, group: "basic" },
  { type: "date", Icon: Calendar, group: "basic" },
  { type: "datetime", Icon: CalendarClock, group: "basic" },
  { type: "checkbox", Icon: CheckSquare, group: "basic" },
  { type: "radio", Icon: CircleDot, group: "basic" },
  { type: "select", Icon: List, group: "basic" },
  { type: "multi_select", Icon: ListChecks, group: "basic" },
  { type: "async_select", Icon: Database, group: "advanced" },
  { type: "file", Icon: Upload, group: "advanced" },
  { type: "computed", Icon: Sigma, group: "advanced" },
  { type: "section", Icon: Folder, group: "layout" },
  { type: "repeater", Icon: Repeat, group: "layout" },
];

const GROUP_LABELS: Record<PaletteItem["group"], string> = {
  basic: "Cơ bản",
  advanced: "Nâng cao",
  layout: "Bố cục",
};

export function Palette(): JSX.Element {
  const addField = useFormBuilderStore((s) => s.addField);
  const groups: PaletteItem["group"][] = ["basic", "advanced", "layout"];

  return (
    <aside className="w-56 shrink-0 border-r bg-muted/20 overflow-y-auto">
      <div className="px-3 py-2 border-b bg-background sticky top-0 z-10">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase">
          Field Palette
        </h2>
      </div>
      <div className="p-2 space-y-3">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1.5 px-1">
              {GROUP_LABELS[group]}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {ITEMS.filter((it) => it.group === group).map((it) => (
                <PaletteCard
                  key={it.type}
                  Icon={it.Icon}
                  label={FIELD_TYPE_LABELS[it.type]}
                  onClick={() => addField(null, it.type)}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/x-field-type", it.type);
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

interface PaletteCardProps {
  Icon: LucideIcon;
  label: string;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}

function PaletteCard({ Icon, label, onClick, onDragStart }: PaletteCardProps): JSX.Element {
  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      title={`Click hoặc kéo thả ${label}`}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 rounded-md border bg-background py-2.5 px-1",
        "text-[11px] leading-tight text-center",
        "hover:border-primary/50 hover:bg-gradient-brand-soft hover:shadow-sm",
        "active:scale-[0.97] transition-all cursor-grab active:cursor-grabbing",
      )}
    >
      <Icon className="size-5 text-muted-foreground" />
      <span className="line-clamp-2">{label}</span>
    </button>
  );
}
